import { failureText, isReadableServerText } from './helpers/errorText'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'data' in error
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error != null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  )
}

/**
 * Type predicate to narrow an unknown error to a FetchBaseQueryError with a message property in data
 */
export function isErrorWithDataMessage(
  error: unknown,
): error is FetchBaseQueryError & { data: { message: string } } {
  return (
    isFetchBaseQueryError(error) &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'message' in error.data &&
    typeof (error.data as any).message === 'string'
  )
}

/**
 * Defensive extraction of a human-readable error message from any value
 * thrown out of an RTK Query mutation/query (`.unwrap()` rejections,
 * caught network errors, etc).
 *
 * Always returns a string. Backend error envelopes vary — some send
 * `{ error: "..." }`, others `{ message: "..." }`, others `{ code, message: {...} }`
 * where `message` is itself an object. Storing a non-string in component
 * state and rendering it as a JSX child triggers React error #31.
 *
 * Pass a fallback (already translated) so the caller controls the user-facing wording.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (isFetchBaseQueryError(err)) return failureText(err) ?? fallback
  // A thrown JS error ("TypeError: Failed to fetch") is not for the user.
  if (isErrorWithMessage(err) && isReadableServerText(err.message)) return err.message
  return fallback
}
